import { produce } from 'immer'
import {
  GET_ORG_MEMBER_FAILURE,
  GET_ORG_MEMBER_INIT,
  GET_ORG_MEMBER_SUCCESS,
  GET_ORG_MEMBERSHIP_FAILURE,
  GET_ORG_MEMBERSHIP_INIT,
  GET_ORG_MEMBERSHIP_SUCCESS,
  GET_PERSON_FAILURE_ACTION,
  GET_PERSON_INIT_ACTION,
  GET_PERSON_SUCCESS_ACTION,
  GET_USER_INVITE_FAILURE_ACTION,
  GET_USER_INVITE_INIT_ACTION,
  GET_USER_INVITE_SUCCESS_ACTION,
  GetOrgMemberActions,
  GetOrgMembershipActions,
  GetPersonActions,
  GetUserInviteActions,
} from 'src/app/PeopleDetailFlyout/redux/peopleDetailFlyout.actions'
import { BookkeeperAccountVisibilityTypes } from 'src/enums'
import { UserDepartment } from 'src/models/user.models'
import {
  GET_CREDIT_CARDS_FAILURE,
  GET_CREDIT_CARDS_INIT,
  GET_CREDIT_CARDS_SUCCESS,
} from 'src/redux/actions/data/creditCards.actions'
import {
  PUT_ORGANIZATION_ROLE_FAILURE,
  PUT_ORGANIZATION_ROLE_INIT,
  PUT_ORGANIZATION_ROLE_SUCCESS,
  PutOrganizationRoleActions,
  RESET_PUT_ORGANIZATION_ROLE_ERROR,
} from 'src/redux/actions/data/organizations.actions'
import {
  PATCH_ORG_MEMBER_FAILURE,
  PATCH_ORG_MEMBER_INIT,
  PATCH_ORG_MEMBER_SUCCESS,
  PatchOrgMemberActions,
  RESET_PATCH_ORG_MEMBER_ERROR,
} from 'src/redux/actions/data/users.actions'
import {
  Actions as CreditCardsFullPaginatedDataActions,
  CreditCardsFullPaginatedDataState,
  initialState as initialCreditCardsFullPaginatedDataState,
} from 'src/redux/creditCardsFullPaginatedData.reducer'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export interface BookkeeperAccountVisibility {
  type: BookkeeperAccountVisibilityTypes
  ids?: string[]
}

export interface PeopleDetailFlyoutState {
  fetchSuccess: boolean
  fetchFailure: boolean
  fetchUserInviteSuccess: boolean
  fetchUserInviteFailure: boolean
  loading: boolean
  creditCardsLoading: boolean
  loadingPersonOrgRole: boolean
  updatePersonOrgRoleTopLevelError?: string
  patchingPeopleDetailInfoForm: boolean
  patchingPeopleDetailInfoErrorDetailMap?: ErrorDetailMap<string>
  creditCardsFullPaginatedData: CreditCardsFullPaginatedDataState
  accountVisibility?: BookkeeperAccountVisibility
  department?: UserDepartment
}

export const initialState: PeopleDetailFlyoutState = {
  fetchSuccess: false,
  fetchFailure: false,
  fetchUserInviteSuccess: false,
  fetchUserInviteFailure: false,
  loading: false,
  creditCardsLoading: false,
  loadingPersonOrgRole: false,
  patchingPeopleDetailInfoForm: false,
  creditCardsFullPaginatedData: initialCreditCardsFullPaginatedDataState,
  accountVisibility: {
    type: BookkeeperAccountVisibilityTypes.ALL,
    ids: [],
  },
}

export type Actions =
  | GetOrgMemberActions
  | GetPersonActions
  | GetUserInviteActions
  | PutOrganizationRoleActions
  | PatchOrgMemberActions
  | CreditCardsFullPaginatedDataActions
  | GetOrgMembershipActions

const PeopleDetailReducer = (
  state = initialState,
  action: Actions,
): PeopleDetailFlyoutState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case GET_ORG_MEMBER_INIT:
      case GET_PERSON_INIT_ACTION:
      case GET_ORG_MEMBERSHIP_INIT: {
        draft.loading = true
        draft.fetchSuccess = false
        draft.fetchFailure = false
        return draft
      }

      case GET_ORG_MEMBER_SUCCESS:
      case GET_PERSON_SUCCESS_ACTION: {
        draft.loading = false
        draft.fetchSuccess = true
        return draft
      }

      case GET_CREDIT_CARDS_INIT: {
        draft.creditCardsLoading = true
        draft.fetchSuccess = false
        draft.fetchFailure = false
        return draft
      }

      case GET_CREDIT_CARDS_SUCCESS: {
        draft.creditCardsLoading = false
        draft.fetchSuccess = true
        draft.creditCardsFullPaginatedData.ids = [
          ...draft.creditCardsFullPaginatedData.ids,
          ...action.payload.creditCards.map((cc) => cc.id),
        ]
        draft.creditCardsFullPaginatedData.complete =
          action.payload.pagination.page >=
          action.payload.pagination.numberOfPages - 1
        draft.creditCardsFullPaginatedData.page = action.payload.pagination.page
        draft.creditCardsFullPaginatedData.numPages =
          action.payload.pagination.numberOfPages
        draft.creditCardsFullPaginatedData.totalItems =
          action.payload.pagination.totalItems
        return draft
      }

      case GET_ORG_MEMBERSHIP_SUCCESS: {
        draft.loading = false
        draft.fetchSuccess = true
        draft.accountVisibility =
          action.payload.organizationMembership.accountVisibility
        draft.department = action.payload.organizationMembership.department
        return draft
      }

      case GET_ORG_MEMBER_FAILURE:
      case GET_PERSON_FAILURE_ACTION:
      case GET_ORG_MEMBERSHIP_FAILURE: {
        draft.loading = false
        draft.fetchFailure = true
        return draft
      }

      case GET_CREDIT_CARDS_FAILURE: {
        draft.creditCardsLoading = false
        draft.fetchFailure = true
        return draft
      }

      case GET_USER_INVITE_INIT_ACTION: {
        draft.loading = true
        draft.fetchUserInviteSuccess = false
        draft.fetchUserInviteFailure = false
        return draft
      }

      case GET_USER_INVITE_SUCCESS_ACTION: {
        draft.loading = false
        draft.fetchUserInviteSuccess = true
        return draft
      }

      case GET_USER_INVITE_FAILURE_ACTION: {
        draft.loading = false
        draft.fetchUserInviteFailure = true
        return draft
      }

      case PUT_ORGANIZATION_ROLE_INIT: {
        draft.updatePersonOrgRoleTopLevelError = undefined
        draft.loadingPersonOrgRole = true
        return draft
      }

      case PUT_ORGANIZATION_ROLE_SUCCESS: {
        draft.updatePersonOrgRoleTopLevelError = undefined
        draft.loadingPersonOrgRole = false
        draft.patchingPeopleDetailInfoForm = false
        return draft
      }

      case PUT_ORGANIZATION_ROLE_FAILURE: {
        draft.updatePersonOrgRoleTopLevelError = action.payload.error
        draft.loadingPersonOrgRole = false
        return draft
      }

      case PATCH_ORG_MEMBER_INIT: {
        draft.patchingPeopleDetailInfoForm = true
        draft.patchingPeopleDetailInfoErrorDetailMap = undefined
        return draft
      }

      case PATCH_ORG_MEMBER_SUCCESS: {
        draft.patchingPeopleDetailInfoForm = false
        return draft
      }

      case PATCH_ORG_MEMBER_FAILURE: {
        const errors = parseBackendErrors(action.payload)
        draft.patchingPeopleDetailInfoForm = false
        draft.patchingPeopleDetailInfoErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case RESET_PATCH_ORG_MEMBER_ERROR: {
        draft.patchingPeopleDetailInfoErrorDetailMap = undefined
        return draft
      }

      case RESET_PUT_ORGANIZATION_ROLE_ERROR: {
        draft.updatePersonOrgRoleTopLevelError = undefined
      }
    }
  })
}

export default PeopleDetailReducer
